/**
 * Auth API functions (api-contract.md §2). Typed wrappers over the fetch client.
 *
 * Bearer-only auth: every login returns a JWT; the client attaches Authorization automatically.
 * The unified entry point is phone-first — check the phone, then password OR OTP, then (for a
 * brand-new account) set an initial password.
 */

import { apiFetch, newIdempotencyKey } from "./client";
import { getRefreshToken } from "../auth/tokenStore";

export interface AuthUser {
  id: string;
  role: "patient" | "doctor" | "pharmacist" | "admin" | "support";
  full_name: string;
  must_change_password: boolean;
  is_new: boolean;
  password_set: boolean;
}

export interface AuthResult {
  user: AuthUser;
  access_token?: string | null;
  refresh_token?: string | null;
  expires_at?: string | null;
}

// --- Unified phone-first login (§2.5) ---

export function checkPhone(
  role: "patient" | "doctor" | "pharmacist" | "admin" | "support",
  phone: string,
): Promise<{
  exists: boolean;
  has_password: boolean;
  // Set when the phone is registered under a DIFFERENT role than the one requested — the
  // screen must block signup and surface "already registered to another account type".
  registered_other_role: boolean;
  // Which other role is never said: the check is unauthenticated (AUTH-20).
}> {
  return apiFetch("/api/v1/auth/phone/check", {
    method: "POST",
    body: { role, phone },
  });
}

export function phoneLogin(
  phone: string,
  password: string,
): Promise<AuthResult> {
  return apiFetch("/api/v1/auth/phone/login", {
    method: "POST",
    body: { phone, password },
  });
}

export function setInitialPassword(new_password: string): Promise<AuthUser> {
  return apiFetch("/api/v1/auth/phone/set-password", {
    method: "POST",
    body: { new_password },
  });
}

// --- Patient OTP (§2.1) ---

export function requestOtp(
  phone: string,
  // Who is signing in: for a doctor/pharmacist the API sends a code only to a number with that
  // account (NO_ACCOUNT otherwise), so their sign-in never creates a patient account (AUTH-09).
  role?: string,
  // dev_otp is returned off-prod only, while Twilio SMS is not yet integrated (see backend
  // OtpRequestOut.dev_otp). The UI shows it in a toast; it will be null once SMS is live.
): Promise<{ request_id: string; expires_in: number; dev_otp: string | null }> {
  return apiFetch("/api/v1/auth/patient/otp/request", {
    method: "POST",
    body: role ? { phone, role } : { phone },
  });
}

export function verifyOtp(
  request_id: string,
  code: string,
): Promise<AuthResult> {
  return apiFetch("/api/v1/auth/patient/otp/verify", {
    method: "POST",
    body: { request_id, code },
  });
}

// --- Self-service unlock (§2.5) ---

/**
 * Lift a too-many-failed-attempts lockout. The user first requests an OTP via
 * `requestOtp`, then verifies it here. No session is issued — the user logs in
 * with their password afterwards.
 */
export function unlockAccount(
  request_id: string,
  code: string,
): Promise<{ unlocked: boolean; status: string }> {
  return apiFetch("/api/v1/auth/unlock/verify", {
    method: "POST",
    body: { request_id, code },
  });
}

/**
 * Forgot-password reset: the user requests an OTP via `requestOtp`, then submits the code
 * together with a new password. Overwrites the password, clears any lockout, and revokes all
 * sessions. No session is issued — the user signs in afterwards with the new password.
 */
export function resetPassword(
  request_id: string,
  code: string,
  new_password: string,
): Promise<{ reset: boolean }> {
  return apiFetch("/api/v1/auth/password/reset", {
    method: "POST",
    body: { request_id, code, new_password },
  });
}

// --- Public account deletion (DPDP P-16, the /legal/data-deletion page) ---

export interface AccountDeletion {
  id: string;
  status: "pending" | "approved";
  created_at: string;
  /** A request was already in progress — it is returned instead of a new one. */
  already_requested: boolean;
  /** Days within which the request is reviewed. */
  review_within_days: number;
}

/** Send a verification code to a registered number (any role). No sign-in needed. */
export function requestDeletionOtp(phone: string): Promise<{
  request_id: string;
  expires_in: number;
  channel: "whatsapp" | "sms" | null;
  dev_otp: string | null;
}> {
  return apiFetch("/api/v1/auth/account-deletion/otp", {
    method: "POST",
    body: { phone },
  });
}

/** Verify the code and file the deletion request for admin review. No session is issued. */
export function confirmAccountDeletion(
  request_id: string,
  code: string,
  reason: string,
): Promise<AccountDeletion> {
  return apiFetch("/api/v1/auth/account-deletion/confirm", {
    method: "POST",
    body: { request_id, code, reason },
  });
}

// --- Staff login (§2.2) ---

export function staffLogin(
  email: string,
  password: string,
): Promise<AuthResult> {
  return apiFetch("/api/v1/auth/staff/login", {
    method: "POST",
    body: { email, password },
  });
}

// --- Signup consume (§2.3) ---

export function consumeSignup(
  token: string,
  new_password: string,
): Promise<AuthResult> {
  return apiFetch("/api/v1/auth/signup/consume", {
    method: "POST",
    body: { token, new_password },
  });
}

// --- Change password (§2.4) ---

/** Every other session is signed out; this one carries on with the fresh tokens (AUTH-04). They
 * are null when there was no session to replace — sign in again then. */
export interface ChangePasswordResult {
  message: string;
  access_token?: string | null;
  refresh_token?: string | null;
  expires_at?: string | null;
}

export function changePassword(
  current_password: string,
  new_password: string,
): Promise<ChangePasswordResult> {
  return apiFetch("/api/v1/auth/change-password", {
    method: "POST",
    body: { current_password, new_password },
  });
}

/**
 * End a session this page was just given but won't use — a sign-in on another role's card (AUTH-18).
 * Nothing was stored, so the refresh token identifies the session (AUTH-11).
 */
export function discardSession(refresh_token: string): Promise<void> {
  return apiFetch("/api/v1/auth/logout", { method: "POST", body: { scope: "current", refresh_token } });
}

// --- Password rule (AUTH-15) ---

export interface PasswordPolicy {
  summary: string;
  min_length: number;
  max_length: number;
  /** Each rule's `pattern` is searched for in the password (`new RegExp(pattern, "u")`). */
  rules: { key: string; label: string; pattern: string }[];
}

/** The rule every new password must meet. Public: forms show it before anyone is signed in. */
export function getPasswordPolicy(): Promise<PasswordPolicy> {
  return apiFetch("/api/v1/auth/password-policy");
}

// --- Logout (§2.4) ---

export function logoutApi(scope: "current" | "all" = "current"): Promise<void> {
  // The refresh token lets the API end this session even when the access token has expired
  // (AUTH-11) — e.g. signing out after the tab sat idle past the session's end.
  const refresh_token = getRefreshToken() ?? undefined;
  return apiFetch("/api/v1/auth/logout", { method: "POST", body: { scope, refresh_token } });
}

// --- Current user (§3.1) ---

export function fetchMe(): Promise<{
  id: string;
  role: AuthUser["role"];
  full_name: string;
  must_change_password: boolean;
}> {
  return apiFetch("/api/v1/me");
}

// --- Device registration (§3.1, used by the WebView shell; 🔑) ---

export function registerDevice(input: {
  platform: "ios" | "android" | "web";
  fcm_token: string;
  app_version?: string;
  device_label?: string;
}): Promise<{ id: string }> {
  return apiFetch("/api/v1/me/devices", {
    method: "POST",
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Stop pushes to one registered device (web sign-out). */
export function revokeDevice(id: string): Promise<void> {
  return apiFetch(`/api/v1/me/devices/${id}`, { method: "DELETE" });
}
